import React from 'react';
import {
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@material-ui/core';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';

import FormSectionCard from './FormSectionCard';
import { FormState } from '../../../hooks/useFormState.types';
import { EnergyConsultationFormModel } from '../types';
import { useTranslation } from '../../../hooks/useTranslation';
import PhoneTextField from '../../phone-text-field';
import EmailTextField from '../../email-text-field';
import TextField from '../../text-field';
import {
  PreferredContactMethod,
  PreferredLanguage,
} from '../../../__generated__/pge-types';

interface Props {
  form: FormState<EnergyConsultationFormModel>;
  isPublicForm?: boolean;
}

const AccountHolderInformationSection: React.FC<Props> = ({
  form,
  isPublicForm = false,
}) => {
  const { t } = useTranslation();

  const preferredLanguages = [
    {
      label: t('ENGLISH'),
      value: PreferredLanguage.English,
    },
    {
      label: t('SPANISH'),
      value: PreferredLanguage.Spanish,
    },
  ];

  return (
    <FormSectionCard title={t('ACCOUNT_HOLDER_INFORMATION')}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="outlined"
            label={t('ACCOUNT_HOLDER_NAME')}
            disabled={!isPublicForm}
            {...form.props('accountHolderName')}
            InputLabelProps={{
              shrink: !!form.values.accountHolderName,
            }}
          />
        </Grid>

        {!isPublicForm && form.values.isRequestorNameFieldsVisible && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label={t('REQUESTOR_FIRSTNAME')}
                {...form.props('requestorFirstName')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label={t('REQUESTOR_LASTNAME')}
                {...form.props('requestorLastName')}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="outlined"
            label={t('JOB_TITLE')}
            {...form.props('jobTitle')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <EmailTextField
            fullWidth
            variant="outlined"
            label={t('EMAIL_ADDRESS')}
            {...form.props('emailAddress')}
            error={!!form.errors.emailAddress}
            helperText={form.errors.emailAddress}
            InputLabelProps={{
              shrink: !!form.values.emailAddress,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <PhoneTextField
            fullWidth
            variant="outlined"
            label={t('PHONE_NUMBER')}
            {...form.props('phoneNumber')}
            InputLabelProps={{
              shrink: !!form.values.phoneNumber,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormLabel component="legend">
            {t('PREFERRED_CONTACT_METHOD')}
          </FormLabel>

          <RadioGroup
            row
            value={form.values.preferredContactMethod}
            onChange={event =>
              form.setValue('preferredContactMethod', event.target.value)
            }
          >
            <FormControlLabel
              value={PreferredContactMethod.Email}
              control={<Radio color="primary" />}
              label={
                <>
                  <EmailOutlinedIcon
                    fontSize="small"
                    style={{
                      verticalAlign: 'middle',
                      marginRight: 4,
                    }}
                  />
                  {t('EMAIL')}
                </>
              }
            />

            <FormControlLabel
              value={PreferredContactMethod.Phone}
              control={<Radio color="primary" />}
              label={
                <>
                  <PhoneOutlinedIcon
                    fontSize="small"
                    style={{
                      verticalAlign: 'middle',
                      marginRight: 4,
                    }}
                  />
                  {t('PHONE')}
                </>
              }
            />
          </RadioGroup>
        </Grid>

        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            variant="outlined"
            label={t('PREFERRED_LANGUAGE')}
            {...form.props('preferredLanguage')}
            error={!!form.errors.preferredLanguage}
            helperText={form.errors.preferredLanguage}
          >
            {preferredLanguages.map(language => (
              <MenuItem key={language.value} value={language.value}>
                {language.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </FormSectionCard>
  );
};

export default AccountHolderInformationSection;
